/*
 * Where the signed-in app begins.
 *
 * App.jsx already draws this line, structurally: every platform route is nested
 * inside <PlatformApp> → <ProtectedRoute> → <Layout>, and everything above that
 * block is public. Structure is not readable at runtime, though, and one thing
 * does need to ask the question while the app is running — the marketing
 * homepage installs fixture responses on the shared axios client while its live
 * product surfaces are mounted, and that must be impossible anywhere a real
 * request could be in flight.
 *
 * So this is the same line written as data. It is not allowed to become a second
 * list: platformRoutes.test.js parses App.jsx, pulls the routes out of the
 * <PlatformApp> block, and fails if any of them is not covered here. App.jsx
 * stays the source of truth; this is a projection of it that a function can read.
 */

/** Route prefixes that belong to the signed-in application. */
export const PLATFORM_ROUTE_PREFIXES = [
  '/dashboard',
  '/settings/marketplace-sandbox',
  '/deploy',
  '/integrations',
  '/credits',
  '/account',
  '/models',
  '/workspaces',
  '/workflows',
  '/ai-agents',
  '/voice-infrastructure',
  '/ai-chat',
  '/ai-sessions',
  '/invocations',
  '/routing-rules',
  /* Only the review queue and the seller workspace. The marketplace catalogue
   * itself (/marketplace, /marketplace/:offerType, /use/:slug) is public and
   * must stay on the marketing side of this line. */
  '/marketplace/review',
  '/marketplace/sell',
  '/admin/routing-policies',
  '/admin/workspace-memory',
  '/cloud-manager',
  '/white-label-request',
  '/white-label',
  '/billing',
  '/admin',
];

/*
 * Routes that are public pages but still talk to the gateway as themselves.
 * They are not under <Layout>, so the structural rule above does not catch
 * them, and a fixture adapter installed over a login POST would be a very bad
 * bug indeed.
 */
export const AUTH_ROUTE_PREFIXES = [
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/verify-email',
];

const startsWithSegment = (pathname, prefix) =>
  pathname === prefix || pathname.startsWith(`${prefix}/`);

export function isPlatformPath(pathname) {
  const path = normalizePathname(pathname);
  return PLATFORM_ROUTE_PREFIXES.some((prefix) => startsWithSegment(path, prefix));
}

export function isAuthPath(pathname) {
  const path = normalizePathname(pathname);
  return AUTH_ROUTE_PREFIXES.some((prefix) => startsWithSegment(path, prefix));
}

/**
 * True only for a page that has no session of its own — the marketing site.
 *
 * Written as "not platform and not auth" rather than as a list of marketing
 * routes, because marketing routes are open-ended (`/:slug` catches every
 * article) while the app's are enumerable. A new article must not need an entry
 * here; a new platform route must.
 */
export function isMarketingPath(pathname) {
  const path = normalizePathname(pathname);
  if (!path) return false;
  return !isPlatformPath(path) && !isAuthPath(path);
}

function normalizePathname(pathname) {
  if (typeof pathname !== 'string' || !pathname) return '';
  const path = pathname.split('?')[0].split('#')[0];
  if (path.length > 1 && path.endsWith('/')) return path.slice(0, -1);
  return path;
}
